import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { ArrowRight, Plus, Trash2, X } from 'lucide-react'
import toast from 'react-hot-toast'

interface Props {
  unitType: 'class' | 'course' | 'group'
  unitId: number
  unitName?: string
  onClose: () => void
}

function getSuccessorDisplayName(item: any) {
  return item.class_name || item.course_name || item.group_name || `#${item.successor_id}`
}

export default function SuccessorEditModal({ unitType, unitId, unitName, onClose }: Props) {
  const queryClient = useQueryClient()
  const [selectedSuccessorId, setSelectedSuccessorId] = useState<string>('')
  const typeLabel = unitType === 'class' ? 'Klasse' : unitType === 'course' ? 'Kurs' : 'Gruppe'

  const { data: successorsData, isLoading } = useQuery(
    ['unit-successors-edit', unitType, unitId],
    () => api.get(`/units/${unitType}/${unitId}/successors`).then(res => res.data),
    { staleTime: 30_000 }
  )
  const successors = successorsData?.data || []

  const { data: unitsData } = useQuery(
    ['units-list-edit-successor', unitType],
    () => api.get(`/units/${unitType}`, { params: { is_active: 'true' } }).then(res => res.data),
    { staleTime: 60_000 }
  )
  const unitNameKey = unitType === 'class' ? 'class_name' : 'name'
  const allUnits = (unitsData?.data || []).filter((u: any) => u.id !== unitId)

  const addSuccessorMutation = useMutation(
    (successorId: number) =>
      api.post(`/units/${unitType}/${unitId}/successors`, {
        successor_type: unitType,
        successor_id: successorId
      }),
    {
      onSuccess: () => {
        setSelectedSuccessorId('')
        toast.success('Folgebeziehung angelegt')
        queryClient.invalidateQueries(['unit-successors-edit', unitType, unitId])
        queryClient.invalidateQueries(['transitions-successor-map'])
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Folgebeziehung konnte nicht angelegt werden')
      }
    }
  )

  const removeSuccessorMutation = useMutation(
    (successorRowId: number) => api.delete(`/units/${unitType}/${unitId}/successors/${successorRowId}`),
    {
      onSuccess: () => {
        toast.success('Folgebeziehung entfernt')
        queryClient.invalidateQueries(['unit-successors-edit', unitType, unitId])
        queryClient.invalidateQueries(['transitions-successor-map'])
      },
      onError: () => {
        toast.error('Folgebeziehung konnte nicht entfernt werden')
      }
    }
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
      <div className="card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Folgeeinheiten bearbeiten: {unitName || `${typeLabel} #${unitId}`}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Wird geladen...</p>
          ) : (
            <div className="space-y-2">
              {successors.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">Noch keine Folgeeinheit hinterlegt.</p>
              ) : (
                successors.map((successor: any) => (
                  <div key={successor.id} className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <ArrowRight className="h-4 w-4 text-primary-600" />
                      <span>{getSuccessorDisplayName(successor)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSuccessorMutation.mutate(successor.id)}
                      className="btn btn-secondary btn-sm"
                      disabled={removeSuccessorMutation.isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50">Neue Folgeeinheit</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <select
                value={selectedSuccessorId}
                onChange={(e) => setSelectedSuccessorId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              >
                <option value="">Bitte auswählen...</option>
                {allUnits.map((unit: any) => (
                  <option key={unit.id} value={unit.id}>
                    {unit[unitNameKey]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedSuccessorId || addSuccessorMutation.isLoading}
                onClick={() => addSuccessorMutation.mutate(parseInt(selectedSuccessorId, 10))}
              >
                <Plus className="h-4 w-4" />
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
